/**
Project: Voice Panel (c)
Title: Service Number Page 
Description: Component for displaying the list of resource accounts with search, sort and pagination.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Flex,
  Text,
  Input,
  Button,
  Table,
  TableRow,
  SearchIcon,
  CloseIcon,
  Dialog,
  RetryIcon,
  Tooltip,
} from "@fluentui/react-northstar";
import { Grid, Row, Col } from "react-flexbox-grid";
import axios from "axios";
import PageUtil from "../../../../core/helpers/page-util";
import IntlUtil from "../../../../core/helpers/intl-util";
import ResponseUtil from "../../../../core/helpers/response-util";
import PageLoader from "../../common/helpers/page-loader";
import AlertInfoMessage from "../../common/helpers/alert-info-message";
import AlertErrorMessage from "../../common/helpers/alert-error-message";
import PageExitGuard from "../../common/helpers/page-exit-guard";
import TableHeaderColumn from "../../common/helpers/table-header-column";
import ServiceNumberRow from "./service-number-row";
import { AppConfigProps } from "../../../../core/settings/app-config";
import { VoiceAdminURLProps } from "../../common/settings/voice-admin-urls";
import { manageError } from "../../../../core/actions/common-actions";
import {
  showPageWaitDialog,
  hidePageWaitDialog,
  getTenantAccountsNumberSyncNow,
} from "../../common/actions/general-actions";
import {
  getTenentResorces,
  getTenentResorcesBulk,
  geSubscriberAccountNumbers,
  setServiceNumbersAllRecords,
  setServiceNumbersDisplayRecords,
  searchServiceNumbers,
  sortServiceNumbers,
  setServiceNumberPhoneNumberToAlter,
  setServiceNumberRoutingPolicyToAlter,
  setServiceNumbersUpdateRecords,
  updateServiceNumberPhoneNumber,
  updateServiceNumberRoutingPolicy,
  updateTenentResorcesBulk,
  resetServiceNumbersData,
} from "../actions/service-number-actions";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import { ServiceNumbersTableHeaderColumns } from "../settings/service-numbers-table-header-columns";
import TokenUtil from "../../../../core/helpers/token-util";
import ServiceNumbersStatus from "./service-number-status";
import moment from "moment";
import _ from "lodash";
import NumbersSyncDialog from "../../common/helpers/numbers-sync-dialog";

class ServiceNumberPage extends Component {
  _isMounted = false;
  _axiosSource = axios.CancelToken.source();
  _cancelToken = { cancelToken: this._axiosSource.token };
  _intl_ns_common = "common";
  _intl_ns_vpa_common = "vpa_common";
  _intl_ns_vpa_servicenumbers = "vpa_servicenumbers";

  constructor(props) {
    super(props);
    this.state = {
      isPageDataFetched: false,
      isListDataFetched: false,
      searchValue: "",
      searchKeyword: "",
      sortedColumn: "name",
      numberExtractType: VoiceAdminConstants.USERS.ASSIGN_STATUS.ALL,
      sortedOrder: VoiceAdminConstants.TABLE_DATA.SORT_ORDER.ASCENDING,
      usersBulkStatus: VoiceAdminConstants.USERS.BULK_STATUS,
      isFormDataFilled: false,
      showDownloadStatusDialog: false,
      serviceNumbersUpdateRecords: [],
      isAlterDataPropagated: true,
      serviceNumbersPropagation: null,
      showAlert: false,
      errorMessage: null,
      errorDetails: null,
      numbersList: [],
      editedNumber: null,
      totalNumbers: [],
      showSyncDialog: false,
      syncNumbersInProgress: false,
      isShowSyncNumbersTooltip: false,
    };
  }

  setStateAsync = (state) => {
    if (this._isMounted) {
      return new Promise((resolve) => {
        this.setState(state, resolve);
      });
    }
  };

  async componentDidMount() {
    this._isMounted = true;
    PageUtil.scrollToTop();
    await this.loadPageData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isShowSyncNumbersTooltip !== prevState.isShowSyncNumbersTooltip && this.state.isShowSyncNumbersTooltip) {
      setTimeout(async () => {
        await this.setStateAsync({ isShowSyncNumbersTooltip: false })
      }, 5000);
    }
  }

  componentWillUnmount() {
    this.props.resetServiceNumbersData();
    this.props.hidePageWaitDialog();
    this._isMounted = false;
    this._axiosSource.cancel(
      IntlUtil.getText(
        this._intl_ns_common,
        "notification.warning.requestCancelled"
      )
    );
  }

  loadPageData = async () => {
    await this.setStateAsync({ isPageDataFetched: false });
    await this.props.hidePageWaitDialog();
    await this.clearAlert();
    await this.resetServiceNumberDataToAlter();
    await this.fetchServiceNumberUsersData();
    await this.fetchSubscriberNumbers();
    await this.setStateAsync({
      isShowSyncNumbersTooltip: this.state.isAlterDataPropagated,
    });
    await this.setStateAsync({ isPageDataFetched: true });
  };

  checkUsersData = async () => {
    if (this.state.serviceNumbersUpdateRecords.length > 0) {
      await this.setStateAsync({ isFormDataFilled: true });
    } else {
      await this.setStateAsync({ isFormDataFilled: false });
    }
  };

  fetchServiceNumberUsersData = async () => {
    await axios
      .all([
        getTenentResorcesBulk(this.state.usersBulkStatus, this._cancelToken),
        getTenentResorces(this._cancelToken),
        getTenantAccountsNumberSyncNow(this._cancelToken),
      ])
      .then(
        axios.spread(
          async (propagationData, serviceNumberData, syncResponse) => {
            if (
              propagationData &&
              propagationData.status === AppConfigProps.httpStatusCode.ok &&
              propagationData.data &&
              serviceNumberData &&
              serviceNumberData.status === AppConfigProps.httpStatusCode.ok &&
              serviceNumberData.data &&
              syncResponse &&
              syncResponse.status === AppConfigProps.httpStatusCode.ok &&
              syncResponse.data
            ) {
              if (propagationData.data.result) {
                const propagation = propagationData.data.result;
                await this.setStateAsync({
                  serviceNumbersPropagation: propagation,
                });
                if (
                  propagation.overallStatus !==
                  VoiceAdminConstants.USN_DATA_PROPAGATION
                    .OVERALL_STATUS_COMPLETED
                ) {
                  await this.setStateAsync({
                    isAlterDataPropagated: false,
                    isCheckPropagatedStatus: true,
                  });
                } else {
                  await this.setStateAsync({ isAlterDataPropagated: true });
                }
              }
              if (serviceNumberData.data.records) {
                let records = [];
                records = records.concat(serviceNumberData.data.records);
                records = await records.map((rec) => ({
                  ...rec,
                  originalPhone: rec.phone,
                  originalPolicyId: rec.policyId,
                  isPhonePopupOpen:false,
                  isPolicyPopupOpen:false
                }));
                await this.setStateAsync({ isListDataFetched: false });
                await this.props.setServiceNumbersAllRecords(records);

                let searchedRecords = await searchServiceNumbers(
                  this.props.serviceNumbersAllRecords,
                  this.state.searchKeyword
                );
                let sortedRecords = await sortServiceNumbers(
                  searchedRecords,
                  this.state.sortedColumn,
                  this.state.sortedOrder
                );
                await this.props.setServiceNumbersDisplayRecords(sortedRecords);
                await this.setStateAsync({
                  isCheckPropagatedStatus: false,
                  isListDataFetched: true,
                });
              }
              if (syncResponse.data.result) {
                const syncNumResponse = syncResponse.data.result;
                await this.setStateAsync({
                  syncTenantAccountRecords: syncNumResponse,
                });
                if (
                  syncNumResponse.processStatus.toLowerCase() !==
                  VoiceAdminConstants.USN_DATA_PROPAGATION.OVERALL_STATUS_COMPLETED.toLowerCase()
                ) {
                  await this.setStateAsync({
                    syncNumbersInProgress: true,
                  });
                } else {
                  await this.setStateAsync({
                    syncNumbersInProgress: false,
                  });
                }
              }
            }
          }
        )
      )
      .catch(async (err) => {
        await this.setStateAsync({ isPageDataFetched: true });
        const errorStatus = ResponseUtil.getStatus(err);
        if (
          errorStatus &&
          errorStatus === AppConfigProps.httpStatusCode.badRequest
        ) {
          const primeError = ResponseUtil.getPrimeError(err);
          if (
            primeError &&
            primeError.code ===
            VoiceAdminConstants.SERVER_ERROR_CODE.VOICE_SETUP_NOTCOMPLETED
          ) {
            this.props.history.push(VoiceAdminURLProps.setup);
            return;
          }
          if (
            primeError &&
            primeError.code ===
            VoiceAdminConstants.SERVER_ERROR_CODE
              .SERVICE_NUMBERS_MICROSOFT_TENANT
          ) {
            await this.setStateAsync({
              errorDetails: primeError.message,
            });
          }

          await this.setStateAsync({
            errorMessage: IntlUtil.getText(
              this._intl_ns_vpa_servicenumbers,
              "notification.error.usersDataRetrievingFailed"
            ),
            showAlert: true,
          });
        } else {
          await manageError(err, this.props.history);
        }
      });
  };

  fetchSubscriberNumbers = async () => {
    await geSubscriberAccountNumbers(
      this.state.numberExtractType,
      this._cancelToken
    )
      .then(async (res) => {
        if (res && res.data && res.data.records && res.data.records) {
          let unassignedNumbers = [];
          res.data.records.forEach((response) => {
            if (
              response &&
              (response.assignStatus === null ||
                response.assignStatus.toLowerCase() ===
                VoiceAdminConstants.USERS.ASSIGN_STATUS.UNASSIGNED.toLowerCase())
            ) {
              unassignedNumbers.push(response);
            }
          });
          let phoneNumbers = unassignedNumbers.map((phoneNumber) => {
            return { ...phoneNumber, allocateStatus: 0 };
          });
          this.setStateAsync({
            numbersList: phoneNumbers,
            totalNumbers: res.data.records,
          });
        }
      })
      .catch(async (err) => {
        await this.setStateAsync({ isPageDataFetched: true });
        const errorStatus = ResponseUtil.getStatus(err);
        if (
          errorStatus &&
          errorStatus === AppConfigProps.httpStatusCode.badRequest
        ) {
          const primeError = ResponseUtil.getPrimeError(err);
          if (
            primeError &&
            primeError.code ===
            VoiceAdminConstants.SERVER_ERROR_CODE.VOICE_SETUP_NOTCOMPLETED
          ) {
            this.props.history.push(VoiceAdminURLProps.setup);
            return;
          }
        }
        await manageError(err, this.props.history);
      });
  };

  fetchServiceNumberUsersStatus = async () => {
    await getTenentResorcesBulk(this.state.usersBulkStatus, this._cancelToken)
      .then(async (res) => {
        if (
          res &&
          res.status === AppConfigProps.httpStatusCode.ok &&
          res.data
        ) {
          const propagation = res.data.result;
          await this.setStateAsync({
            serviceNumbersPropagation: propagation,
          });
          if (
            propagation.overallStatus !==
            VoiceAdminConstants.USN_DATA_PROPAGATION.OVERALL_STATUS_COMPLETED
          ) {
            await this.setStateAsync({
              isAlterDataPropagated: false,
              isCheckPropagatedStatus: true,
            });
          } else {
            await this.setStateAsync({ isAlterDataPropagated: true });
          }
        }
      })
      .catch(async (err) => {
        await this.setStateAsync({ isPageDataFetched: true });
        await manageError(err, this.props.history);
      });
  };

  openUsersStatusDialog = async () => {
    await this.setStateAsync({ showUsersStatusDialog: true });
  };

  refreshServiceNumberPropagationBulk = async (serviceNumbersPropagation) => {
    await this.getServiceNumbersPropagationInfoText(serviceNumbersPropagation);
    if (
      serviceNumbersPropagation &&
      serviceNumbersPropagation.overallStatus !==
      VoiceAdminConstants.USN_DATA_PROPAGATION.OVERALL_STATUS_COMPLETED
    ) {
      await this.setStateAsync({
        isAlterDataPropagated: false,
        isCheckPropagatedStatus: true,
      });
      await this.setServiceNumberUpdateData(serviceNumbersPropagation?.data);
    } else {
      if (!this.state.isAlterDataPropagated)
        await this.setServiceNumberUpdateData(serviceNumbersPropagation?.data);
      await this.setStateAsync({
        isAlterDataPropagated: true,
        serviceNumbersPropagation: serviceNumbersPropagation,
      });
    }
  };

  renderServicNumberStatusDialog = () => {
    return (
      <ServiceNumbersStatus
        showDialogBox={this.state.showUsersStatusDialog}
        refreshUserPropagationBulk={this.refreshServiceNumberPropagationBulk}
        // setServiceNumberUpdateData={this.setServiceNumberUpdateData}
        history={this.props.history}
        hideDialogBox={async () => {
          await this.setState({ showUsersStatusDialog: false });
          if (this.state.numberRemoved) {
            await this.setStateAsync({ numberRemoved: false });
            await this.setStateAsync({ isShowSyncNumbersTooltip: true });
          }
        }}
        isAlterDataPropagated={this.state.isAlterDataPropagated}
        fetchSubscriberNumbers={this.fetchSubscriberNumbers}
        numberRemovalMessage={IntlUtil.getText(
          this._intl_ns_vpa_servicenumbers,
          "notification.info.numberRemovalMessage"
        )}
      />
    );
  };

  setServiceNumberUpdateData = async (serviceNumberPropagationRecords) => {
    if (
      serviceNumberPropagationRecords &&
      serviceNumberPropagationRecords.length > 0
    ) {
      let serviceNumbersAllRecords = this.props.serviceNumbersAllRecords;
      serviceNumberPropagationRecords.forEach(async (record) => {
        if (
          record.processStatus ===
          VoiceAdminConstants.USN_DATA_PROPAGATION
            .ITEM_PROCESS_STATUS_COMPLETED &&
          record.updateStatus ===
          VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_UPDATE_STATUS_FAILURE
        ) {
          let serviceNumber = {};
          if (record && record.paramName === "Policy") {
            let item = serviceNumbersAllRecords.find((x) => x.id === record.id);
            serviceNumber = {
              ...record,
              paramValue: item.originalPolicyId,
            };
          }
          if (record && record.paramName === "Phone") {
            let item = serviceNumbersAllRecords.find((x) => x.id === record.id);
            serviceNumber = {
              ...record,
              paramValue: item.originalPhone,
            };
          }
          await this.props.setServiceNumbersUpdateRecords(serviceNumber);
        } else if (
          record.processStatus ===
          VoiceAdminConstants.USN_DATA_PROPAGATION
            .ITEM_PROCESS_STATUS_COMPLETED &&
          record.updateStatus ===
          VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_UPDATE_STATUS_SUCCESS
        ) {
          await this.props.setServiceNumbersUpdateRecords(record);
        }
      });

      let numberRemoved =
        serviceNumberPropagationRecords.every(
          (rec) => rec?.processStatus === "Completed"
        ) &&
        serviceNumberPropagationRecords.some(
          (rec) =>
            rec?.paramName === "Phone" &&
            (rec?.actionType === "Remove" ||
              rec?.actionType === "Update" ||
              rec?.actionType === "Import") &&
            rec?.updateStatus === "Success"
        );
      await this.setStateAsync({ numberRemoved });
    }
  };

  handleSearchChange = async (e) => {
    e.preventDefault();
    await this.setStateAsync({ searchValue: e.target.value });
  };

  handleSearchCancel = async (e) => {
    e.preventDefault();
    await this.clearAlert();
    await this.setStateAsync({ isListDataFetched: false });
    let sortedRecords = await sortServiceNumbers(
      this.props.serviceNumbersAllRecords,
      this.state.sortedColumn,
      this.state.sortedOrder
    );
    await this.props.setServiceNumbersDisplayRecords(sortedRecords);

    await this.setStateAsync({ searchKeyword: "", searchValue: "" });
    await this.resetServiceNumberDataToAlter();
    await this.setStateAsync({ isListDataFetched: true });
  };

  handleSearchSubmit = async (e) => {
    e.preventDefault();
    await this.clearAlert();
    await this.setStateAsync({ isListDataFetched: false });
    if (
      this.props.serviceNumbersAllRecords &&
      this.props.serviceNumbersAllRecords.length > 0
    ) {
      let searchKeyword = this.state.searchValue;
      await this.setStateAsync({ searchKeyword: searchKeyword });
      let searchedRecords = await searchServiceNumbers(
        this.props.serviceNumbersAllRecords,
        this.state.searchKeyword
      );

      let sortedRecords = await sortServiceNumbers(
        searchedRecords,
        this.state.sortedColumn,
        this.state.sortedOrder
      );
      await this.props.setServiceNumbersDisplayRecords(sortedRecords);

      await this.resetServiceNumberDataToAlter();
      await this.setStateAsync({ isListDataFetched: true });
    }
  };

  handleSortChange = async (sortColumn, sortOrder) => {
    await this.clearAlert();
    await this.setStateAsync({ isListDataFetched: false });
    let sortedRecords = await sortServiceNumbers(
      this.props.serviceNumbersDisplayRecords,
      sortColumn,
      sortOrder
    );
    await this.props.setServiceNumbersDisplayRecords(sortedRecords);

    await this.setStateAsync({
      sortedColumn: sortColumn,
      sortedOrder: sortOrder,
    });

    await this.setStateAsync({ searchValue: this.state.searchKeyword });
    await this.resetServiceNumberDataToAlter();
    await this.setStateAsync({ isListDataFetched: true });
  };

  resetServiceNumberDataToAlter = async () => {
    await this.props.setServiceNumberPhoneNumberToAlter("");
    await this.props.setServiceNumberRoutingPolicyToAlter("");
  };

  renderPaginationInfo = () => {
    if (
      this.props.serviceNumbersDisplayRecords &&
      this.props.serviceNumbersDisplayRecords.length > this.state.pageLimit
    ) {
      let start = this.state.pageOffset * this.state.pageLimit;
      let end = start + this.state.pageLimit;
      if (end > this.props.serviceNumbersDisplayRecords.length)
        end = this.props.serviceNumbersDisplayRecords.length;
      return (
        <>
          &nbsp;
          {IntlUtil.getSubstituteText(
            this._intl_ns_vpa_servicenumbers,
            "content.displayingStartToEnd",
            [
              {
                key: "<START>",
                value: start + 1,
              },
              {
                key: "<END>",
                value: end,
              },
            ]
          )}
        </>
      );
    } else {
      return null;
    }
  };

  updatePhoneNumber = async (id, email, phoneNumber, allocateStatus) => {
    if (
      id !== null &&
      id.length > 0 &&
      email &&
      email.length > 0 &&
      phoneNumber &&
      phoneNumber.length > 0
    ) {
      await this.props.updateServiceNumberPhoneNumber({
        id: id,
        phone: phoneNumber,
      });
      const dataObj = {
        id: id,
        email: email,
        paramName: "Phone",
        actionType: "Update",
        paramValue: phoneNumber,
      };
      const numObj = {
        phoneNumber: phoneNumber,
        allocateStatus: allocateStatus,
      };
      this.setStateAsync({ editedNumber: null });
      await this.setServiceNumbersChangeRecords(dataObj);
      await this.setServiceNumbersNumberChangeRecords(numObj);
      await this.checkUsersData();
    }
  };

  removePhoneNumber = async (id, email, phoneNumber, allocateStatus) => {
    if (id !== null && id.length > 0 && email && email.length > 0) {
      await this.props.updateServiceNumberPhoneNumber({ id: id, phone: "" });
      const dataObj = {
        id: id,
        email: email,
        paramName: "Phone",
        actionType: "Remove",
        paramValue: null,
      };
      const numObj = {
        phoneNumber: phoneNumber,
        allocateStatus: allocateStatus,
      };
      this.setStateAsync({ editedNumber: null });
      await this.setServiceNumbersChangeRecords(dataObj);
      await this.setServiceNumbersEditNumbersRecords(numObj);
      await this.checkUsersData();
    }
  };

  updateRoutingPolicy = async (id, email, policyId) => {
    if (
      id !== null &&
      id.length > 0 &&
      email &&
      email.length > 0 &&
      policyId &&
      policyId.length > 0
    ) {
      await this.props.updateServiceNumberRoutingPolicy({
        id: id,
        policyId: policyId,
      });
      const dataObj = {
        id: id,
        email: email,
        paramName: "Policy",
        actionType: "Update",
        paramValue: policyId,
      };
      await this.setServiceNumbersChangeRecords(dataObj);
      await this.checkUsersData();
    }
  };

  removeRoutingPolicy = async (id, email) => {
    if (id !== null && id.length > 0 && email && email.length > 0) {
      await this.props.updateServiceNumberRoutingPolicy({
        id: id,
        policyId: "",
      });
      const dataObj = {
        id: id,
        email: email,
        paramName: "Policy",
        actionType: "Remove",
        paramValue: null,
      };
      await this.setServiceNumbersChangeRecords(dataObj);
      await this.checkUsersData();
    }
  };
  setServiceNumbersNumberChangeRecords = async (numObj) => {
    if (numObj && this.state.numbersList) {
      let numItem = null;
      let numIndex = null;
      await this.state.numbersList.forEach((num, index) => {
        if (num.phoneNumber === numObj.phoneNumber) {
          numItem = num;
          numIndex = index;
          return;
        }
      });
      if (numItem) {
        numItem.phoneNumber = numObj.phoneNumber;
        numItem.allocateStatus = numObj.allocateStatus;
        await this.setState((prevState) => {
          const result = [...prevState.numbersList];
          result[numIndex] = numItem;
          return { numbersList: result };
        });
      }
    } else {
      let data = this.state.numbersList;
      // data = data.slice(0);
      // data.splice(0, 0, numObj);
      data.push(numObj);
      await this.setState({
        numbersList: data,
      });
    }
  };

  editPhoneNumber = async (phoneNumber, allocateStatus) => {
    if (phoneNumber && phoneNumber.length > 0) {
      await this.setStateAsync({ isFormDataFilled: true });
      const numObj = {
        phoneNumber: phoneNumber,
        allocateStatus: allocateStatus,
      };
      await this.setServiceNumbersEditNumbersRecords(numObj);
      await this.setStateAsync({ editedNumber: phoneNumber });
      await this.checkUsersData();
    }
  };
  setServiceNumbersEditNumbersRecords = async (numObj) => {
    if (numObj && this.state.numbersList) {
      let numItem = null;
      let numIndex = null;
      await this.state.numbersList.forEach((num, index) => {
        if (num.phoneNumber === numObj.phoneNumber) {
          numItem = num;
          numIndex = index;
          return;
        }
      });
      if (numItem) {
        numItem.allocateStatus = numObj.allocateStatus;
        await this.setState((prevState) => {
          const result = [...prevState.numbersList];
          result[numIndex] = numItem;
          return { numbersList: result };
        });
      } else {
        let data = this.state.numbersList;
        data.push(numObj);
        await this.setState({
          numbersList: data,
        });
      }
    }
  };

  setServiceNumbersChangeRecords = async (dataObj) => {
    if (dataObj && this.state.serviceNumbersUpdateRecords) {
      let serviceNumberItem = null;
      let serviceNumberIndex = null;
      await this.state.serviceNumbersUpdateRecords.forEach(
        (serviceNumber, index) => {
          if (
            serviceNumber.id === dataObj.id &&
            serviceNumber.paramName === dataObj.paramName
          ) {
            serviceNumberItem = serviceNumber;
            serviceNumberIndex = index;
            return;
          }
        }
      );

      if (serviceNumberItem) {
        serviceNumberItem.paramValue = dataObj.paramValue;
        serviceNumberItem.actionType = dataObj.actionType;
        await this.setState((prevState) => {
          const result = [...prevState.serviceNumbersUpdateRecords];
          result[serviceNumberIndex] = serviceNumberItem;
          return { serviceNumbersUpdateRecords: result };
        });
      } else {
        let data = this.state.serviceNumbersUpdateRecords;
        // data = data.slice(0);
        // data.splice(0, 0, dataObj);
        data.push(dataObj);
        await this.setState({
          serviceNumbersUpdateRecords: data,
        });
      }
    }
  };
  setServiceRecords=async()=>{
    let searchedRecords = await searchServiceNumbers(
      this.props.serviceNumbersAllRecords,
      this.state.searchKeyword
    );
    let sortedRecords = await sortServiceNumbers(
      searchedRecords,
      this.state.sortedColumn,
      this.state.sortedOrder
    );
    await this.props.setServiceNumbersDisplayRecords(sortedRecords);
  }
  resetRoutingPolicy = async (id, policyId) => {
    await this.props.updateServiceNumberRoutingPolicy({
      id: id,
      policyId: policyId,
    });
    const serviceNumbersArray = this.state.serviceNumbersUpdateRecords;
    const dataObj = await serviceNumbersArray.filter((users) => {
      if (users.id === id && users.paramName === "Policy") {
        return false;
      } else {
        return true;
      }
    });
    await this.setStateAsync({ serviceNumbersUpdateRecords: dataObj });
    await this.checkUsersData();
  };

  resetPhoneNumber = async (id, phoneNumber) => {
    await this.props.updateServiceNumberPhoneNumber({
      id: id,
      phone: phoneNumber,
    });
    const serviceNumbersArray = this.state.serviceNumbersUpdateRecords;
    if (serviceNumbersArray) {
      const dataObj = await serviceNumbersArray.filter((users) => {
        if (users.id === id && users.paramName === "Phone") {
          return false;
        } else {
          return true;
        }
      });
      await this.setStateAsync({ serviceNumbersUpdateRecords: dataObj });
      await this.checkUsersData();
    }
  };

  changeServiceNumbers = async () => {
    await this.clearAlert();
    if (
      this.state.serviceNumbersUpdateRecords &&
      this.state.serviceNumbersUpdateRecords.length > 0
    ) {
      let serviceNumberUpdateRecords = _.uniqBy(
        this.state.serviceNumbersUpdateRecords,
        (data) => data.email
      );
      if (
        serviceNumberUpdateRecords &&
        serviceNumberUpdateRecords.length <=
        VoiceAdminConstants.USERS.NUMBER_OF_USER_LESS_THAN_ONE_HUNDRED
      ) {
        await this.props.showPageWaitDialog(
          IntlUtil.getText(
            this._intl_ns_vpa_servicenumbers,
            "notification.info.pleaseWaitUpdatingData"
          )
        );
        const data = {
          refreshToken: TokenUtil.getIdentitySippioRefreshToken(),
          data: [...this.state.serviceNumbersUpdateRecords],
        };
        await this.setStateAsync({ serviceNumbersUpdateRecords: [] });
        await updateTenentResorcesBulk(data, this._cancelToken)
          .then(async (res) => {
            if (
              res &&
              res.status === AppConfigProps.httpStatusCode.ok &&
              res.data &&
              res.data.result
            ) {
              await this.fetchServiceNumberUsersStatus();
              await this.fetchSubscriberNumbers();
            } else {
              await this.props.hidePageWaitDialog();
              await this.setStateAsync({ isFormDataFilled: false });
              await manageError(new Error(), this.props.history);
            }
          })
          .catch(async (err) => {
            await this.props.hidePageWaitDialog();
            await this.setStateAsync({ isFormDataFilled: false });
            const errorStatus = ResponseUtil.getStatus(err);
            if (
              errorStatus &&
              errorStatus === AppConfigProps.httpStatusCode.badRequest
            ) {
              const primeError = ResponseUtil.getPrimeError(err);
              if (
                primeError &&
                primeError.code ===
                VoiceAdminConstants.SERVER_ERROR_CODE.VOICE_SETUP_NOTCOMPLETED
              ) {
                this.props.history.push(VoiceAdminURLProps.setup);
                return;
              }

              if (
                primeError &&
                primeError.code ===
                VoiceAdminConstants.SERVER_ERROR_CODE
                  .SERVICE_NUMBERS_UPDATE_ALREADY_INPROGRESS
              ) {
                await this.setStateAsync({
                  errorDetails: primeError.message,
                });
              }

              await this.setStateAsync({
                errorMessage: IntlUtil.getText(
                  this._intl_ns_vpa_servicenumbers,
                  "notification.error.serviceNumbersDataUpdateFailed"
                ),
                showAlert: true,
              });
            } else {
              await manageError(err, this.props.history);
            }
          });
        await this.props.hidePageWaitDialog();
        await this.setStateAsync({ isFormDataFilled: false });
      } else {
        await this.setStateAsync({
          errorMessage: IntlUtil.getText(
            this._intl_ns_vpa_servicenumbers,
            "notification.error.numberOfUsersLessThenOneHundred"
          ),
          showAlert: true,
        });
      }
    }
  };

  clearAlert = async () => {
    await this.setStateAsync({
      showAlert: false,
      errorMessage: null,
      errorDetails: null,
    });
  };

  clearSearchData = async () => {
    await this.setStateAsync({ searchValue: "", searchKeyword: "" });
  };

  getServiceNumbersPropagationInfoText = (propagation) => {
    if (propagation) {
      let propagationTimestamp = propagation.createTimestamp;
      if (
        propagation.endTimestamp &&
        propagation.endTimestamp !== "0001-01-01T00:00:00"
      ) {
        propagationTimestamp = propagation.endTimestamp;
      }
      const timestampObj = moment(propagationTimestamp);
      const dateString = timestampObj.format("ll");
      const timeString = timestampObj.format("LTS");

      let text = IntlUtil.getText(
        this._intl_ns_vpa_servicenumbers,
        "notification.info.serviceNumbersDataPropagationCompleted"
      );
      if (timestampObj && dateString && timeString) {
        text =
          text +
          " " +
          IntlUtil.getSubstituteText(
            this._intl_ns_vpa_servicenumbers,
            "notification.info.serviceNumbersDataPropagationDateTime",
            [
              {
                key: "<DATE>",
                value: dateString,
              },
              {
                key: "<TIME>",
                value: timeString,
              },
            ]
          );
      }
      return text;
    } else return "";
  };

  phoneNumberSort = (numbers) => {
    if (numbers) {
      let sortedPhoneNumbers = _.sortBy(numbers, (number) => {
        return parseInt(number.phoneNumber);
      });

      if (this.state.editedNumber !== null) {
        let selectedNumber = numbers.find(
          (selectedNumber) =>
            selectedNumber.phoneNumber === this.state.editedNumber
        );
        // phoneNumbers = numbers.filter(
        //   (numbers) => numbers.phoneNumber !== this.state.editedNumber // Remove the phoneNumber from the array list
        // );
        if (selectedNumber) {
          sortedPhoneNumbers.unshift(selectedNumber); // add edited selected PhoneNumber in beginning of an array list
        }
      }

      return sortedPhoneNumbers;
    }
  };

  renderNumbersSyncDialog = () => {
    if (this.state.showSyncDialog)
      return (
        <NumbersSyncDialog
          showSyncDialog={this.state.showSyncDialog}
          syncResponse={this.state.syncTenantAccountRecords}
          numberRemoved={this.state.numberRemoved}
          numbersSyncCompleted={this.numbersSyncCompleted}
          closeNumberSyncDialog={this.closeNumberSyncDialog}
          syncResponseUpdate={this.syncResponseUpdate}
          resetNumberRemoval={async () =>
            await this.setStateAsync({ numberRemoved: false })
          }
          dataUpdateInProgress={!this.state.isAlterDataPropagated}
          dataUpdateMessage={IntlUtil.getText(
            this._intl_ns_vpa_servicenumbers,
            "notification.info.serviceNumbersDataUpdateInProgress"
          )}
          dataUpdateMessageTwo={IntlUtil.getText(
            this._intl_ns_vpa_servicenumbers,
            "notification.info.serviceNumbersDataUpdateInProgressTwo"
          )}
          numberRemovalMessage={IntlUtil.getText(
            this._intl_ns_vpa_servicenumbers,
            "notification.info.numberRemovalMessage"
          )}
          syncNumbersInProgressUpdate={this.syncNumbersInProgressUpdate}
          syncNumbersMessage={IntlUtil.getText(
            this._intl_ns_vpa_servicenumbers,
            "notification.info.syncNumbersMessage"
          )}
        />
      );
  };

  syncNumbersInProgressUpdate = async (isInProgress) => {
    await this.setStateAsync({ syncNumbersInProgress: isInProgress });
  };

  syncResponseUpdate = async (syncResponse) => {
    await this.setStateAsync({ syncTenantAccountRecords: syncResponse });
  };

  closeNumberSyncDialog = async () => {
    await this.setStateAsync({ showSyncDialog: false });
    if (this.state.numberRemoved) {
      await this.setStateAsync({ numberRemoved: false });
    }
  };

  numbersSyncCompleted = async () => {
    await this.fetchSubscriberNumbers();
  };
  handleScrollChange=async()=>{
    let serviceRecords=[];
    if (this.props.serviceNumbersAllRecords?.length > 0) {
          this.props.serviceNumbersAllRecords.forEach((service) => {
            
              service.isPhonePopupOpen = false;
              service.isPolicyPopupOpen=false;
            
            serviceRecords.push({ ...service });
          });
          await this.props.setServiceNumbersAllRecords(serviceRecords);
       await this.setServiceRecords();
  }
}
  render() {
    return (
      <PageLoader
        status={this.state.isPageDataFetched}
        size="medium"
        labelPosition="below"
        label={IntlUtil.getText(
          this._intl_ns_common,
          "content.loadingInprogress"
        )}
        type="full-center"
      >
        <>
          <Grid fluid className="page-grid p-t-20">
            <Row>
              <Col xs={12} sm={12} md={12} lg={3} xl={2} className="m-b-10">
                <Input
                  inline
                  inverted
                  placeholder={IntlUtil.getText(
                    this._intl_ns_vpa_servicenumbers,
                    "content.search"
                  )}
                  className="vpa-usn-search-input text-fs-r"
                  value={this.state.searchValue}
                  onChange={this.handleSearchChange}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      this.handleSearchSubmit(e);
                    }
                  }}
                  icon={
                    this.state.searchValue.length === 0 ? (
                      <SearchIcon
                        className="cursor-pointer vpa-usn-search-icon"
                        onClick={this.handleSearchSubmit}
                      />
                    ) : (
                      <CloseIcon
                        className="cursor-pointer vpa-usn-search-icon"
                        onClick={this.handleSearchCancel}
                      />
                    )
                  }
                />
                {/* <Button
                      text
                      className="vpa-usn-search-button"
                      onClick={this.handleSearchSubmit}
                    >
                      <SearchIcon />
                    </Button> */}
              </Col>
              {this.state.showAlert === false ? (
                <Col xs={12} sm={12} md={12} lg={7} xl={8}>
                  {this.state.serviceNumbersPropagation ? (
                    <>
                      {this.state.isAlterDataPropagated === false ? (
                        <Row>
                          <Col xs={12}>
                            <AlertInfoMessage
                              fitted={true}
                              content={IntlUtil.getText(
                                this._intl_ns_vpa_servicenumbers,
                                "notification.info.serviceNumbersDataPropagationInprogress"
                              )}
                              linkDetails={{
                                label: IntlUtil.getText(
                                  this._intl_ns_vpa_servicenumbers,
                                  "content.viewStatus"
                                ),
                                callback: this.openUsersStatusDialog,
                              }}
                            />
                          </Col>
                        </Row>
                      ) : this.state.syncNumbersInProgress ? (
                        <Row>
                          <Col xs={12}>
                            <AlertInfoMessage
                              fitted={true}
                              content={IntlUtil.getText(
                                this._intl_ns_vpa_servicenumbers,
                                "notification.info.syncNumbersInProgress"
                              )}
                              linkDetails={{
                                label: IntlUtil.getText(
                                  this._intl_ns_vpa_servicenumbers,
                                  "content.viewStatus"
                                ),
                                callback: async () =>
                                  await this.setStateAsync({
                                    showSyncDialog: true,
                                  }),
                              }}
                            />
                          </Col>
                        </Row>
                      ) : (
                        <Row>
                          <Col xs={12} lg={10} md={11}>
                            <AlertInfoMessage
                              fitted={true}
                              content={this.getServiceNumbersPropagationInfoText(
                                this.state.serviceNumbersPropagation
                              )}
                              linkDetails={{
                                label: IntlUtil.getText(
                                  this._intl_ns_vpa_servicenumbers,
                                  "content.viewResult"
                                ),
                                callback: this.openUsersStatusDialog,
                              }}
                            />
                            <Dialog
                              backdrop={true}
                              confirmButton={IntlUtil.getText(
                                this._intl_ns_vpa_servicenumbers,
                                "content.ok"
                              )}
                              onConfirm={() => {
                                this.setState({
                                  showDownloadStatusDialog: false,
                                });
                              }}
                              open={this.state.showDownloadStatusDialog}
                              content={
                                <Text
                                  className="vpa-page-dialog-info-text"
                                  content={IntlUtil.getText(
                                    this._intl_ns_vpa_servicenumbers,
                                    "notification.info.downloadServiceNumbers"
                                  )}
                                />
                              }
                              className="vpa-page-dialog"
                            />
                          </Col>
                        </Row>
                      )}
                    </>
                  ) : this.state.syncNumbersInProgress ? (
                    <Row>
                      <Col xs={12}>
                        <AlertInfoMessage
                          fitted={true}
                          content={IntlUtil.getText(
                            this._intl_ns_vpa_servicenumbers,
                            "notification.info.syncNumbersInProgress"
                          )}
                          linkDetails={{
                            label: IntlUtil.getText(
                              this._intl_ns_vpa_servicenumbers,
                              "content.viewStatus"
                            ),
                            callback: async () =>
                              await this.setStateAsync({
                                showSyncDialog: true,
                              }),
                          }}
                        />
                      </Col>
                    </Row>
                  ) : null}
                </Col>
              ) : null}
              {this.state.showAlert === true ? (
                <Col xs={12} sm={12} md={12} lg={7} xl={8}>
                  <Row>
                    <Col xs={12} lg={9}>
                      {this.state.errorMessage ? (
                        <AlertErrorMessage
                          content={this.state.errorMessage}
                          moreDetails={{
                            label: IntlUtil.getText(
                              this._intl_ns_vpa_servicenumbers,
                              "content.moreDetails"
                            ),
                            content: this.state.errorDetails,
                          }}
                        />
                      ) : null}
                    </Col>
                  </Row>
                </Col>
              ) : null}
              <Col xs={12} sm={12} md={12} lg={2} xl={2}>
                <Row start="xs" end="lg xl">
                  <Tooltip
                    onOpenChange={(e, data) =>
                      this.setStateAsync({ isShowSyncNumbersTooltip: data.open })
                    }
                    open={this.state.isShowSyncNumbersTooltip}
                    trigger={
                      <Button
                        content={IntlUtil.getText(
                          this._intl_ns_vpa_servicenumbers,
                          "content.syncNumbers"
                        )}
                        icon={<RetryIcon />}
                        onClick={async () =>
                          await this.setStateAsync({ showSyncDialog: true })
                        }
                        text
                        primary
                        className="m-r-5"
                      />}
                    content={
                      <Text>
                        {IntlUtil.getText(
                          this._intl_ns_vpa_common,
                          "content.tooltip.numbersSync"
                        )}
                      </Text>
                    }
                    align="center"
                    position="below"
                  />
                </Row>
              </Col>
            </Row>

            <Row>
              <Col xs={12} sm={12} md={8} lg={8} className="m-b-5">
                {!this.props.serviceNumbersDisplayRecords ||
                  this.props.serviceNumbersDisplayRecords.length === 0 ? (
                  <Flex>
                    <Text className="page-text text-fs-s text-fc-dark">
                      {IntlUtil.getText(
                        this._intl_ns_vpa_servicenumbers,
                        "content.noServiceNumbersFound"
                      )}
                    </Text>
                  </Flex>
                ) : (
                  <Flex>
                    <Text className="page-text text-fs-s text-fc-dark">
                      {IntlUtil.getSubstituteText(
                        this._intl_ns_vpa_servicenumbers,
                        "content.countServiceNumbersFound",
                        [
                          {
                            key: "<COUNT>",
                            value:
                              this.props.serviceNumbersDisplayRecords.length,
                          },
                        ]
                      )}
                    </Text>

                    <Text className="page-text text-fs-s text-fc-dark">
                      {this.renderPaginationInfo()}
                    </Text>
                  </Flex>
                )}
              </Col>
              {/* <Col xs={12} sm={12} md={4} lg={4} className="m-t-5">
                    {this.state.isFormDataFilled === true ? (
                      <Flex className="vpa-usn-table-legend-wrapper">
                        <Tooltip
                          trigger={
                            <InfoIcon className="vpa-usn-table-legend-alter" />
                          }
                          content={
                            <Text className="vpa-page-tooltip">
                              {IntlUtil.getText(
                                this._intl_ns_vpa_servicenumbers,
                                "content.serviceNumberDataAlterTooltip"
                              )}
                            </Text>
                          }
                          align="start"
                        />
                      </Flex>
                    ) : null}
                  </Col> */}
            </Row>
            <Row>
              <Col xs={12}>
                {this.props.serviceNumbersDisplayRecords &&
                  this.props.serviceNumbersDisplayRecords.length > 0 ? (
                  <div onScroll={this.handleScrollChange}column className="m-t-10 vpa-sn-table-wrapper">
                    <Table aria-label="table">
                      <Flex column className="vpa-usn-table-header-wrapper">
                        <TableRow header className="vpa-usn-table-header-row">
                          {ServiceNumbersTableHeaderColumns(
                            this._intl_ns_vpa_servicenumbers
                          ).map((column) => {
                            return (
                              <TableHeaderColumn
                                {...column}
                                key={column.columnId}
                                sortedColumn={this.state.sortedColumn}
                                sortedOrder={this.state.sortedOrder}
                                sortFunction={this.handleSortChange}
                              />
                            );
                          })}
                        </TableRow>
                      </Flex>
                      <Flex column className="vpa-usn-table-data-wrapper">
                        <PageLoader
                          status={this.state.isListDataFetched}
                          size="medium"
                          type="center"
                        >
                          {this.props.serviceNumbersDisplayRecords.map(
                            (serviceNumber, index) => {
                              return (
                                <ServiceNumberRow
                                  serviceNumber={serviceNumber}
                                  setServiceRecords={this.setServiceRecords}
                                  key={`servicenumber-${index}`}
                                  updatePhoneNumber={this.updatePhoneNumber}
                                  removePhoneNumber={this.removePhoneNumber}
                                  editPhoneNumber={this.editPhoneNumber}
                                  resetPhoneNumber={this.resetPhoneNumber}
                                  updateRoutingPolicy={this.updateRoutingPolicy}
                                  removeRoutingPolicy={this.removeRoutingPolicy}
                                  resetRoutingPolicy={this.resetRoutingPolicy}
                                  totalNumbers={this.state.totalNumbers}
                                  isAlterDataPropagated={
                                    this.state.isAlterDataPropagated
                                  }
                                  intlNamespace={
                                    this._intl_ns_vpa_servicenumbers
                                  }
                                  phoneNumbersList={this.phoneNumberSort(
                                    this.state.numbersList
                                  )}
                                  history={this.props.history}
                                  editedNumber={this.state.editedNumber}
                                />
                              );
                            }
                          )}
                        </PageLoader>
                      </Flex>
                    </Table>
                  </div>
                ) : null}
              </Col>
            </Row>
            {this.props.serviceNumbersDisplayRecords &&
              this.props.serviceNumbersDisplayRecords.length > 0 ? (
              <Row>
                <Col xs={12} className="m-t-20">
                  <Button
                    primary
                    disabled={
                      !this.state.isFormDataFilled ||
                      !this.state.isAlterDataPropagated ||
                      this.state.syncNumbersInProgress
                    }
                    content={IntlUtil.getText(
                      this._intl_ns_vpa_servicenumbers,
                      "content.applyChanges"
                    )}
                    onClick={this.changeServiceNumbers}
                    className="vpa-usn-button"
                  />
                  <PageExitGuard
                    when={this.state.isFormDataFilled === true}
                    navigate={(path) => this.props.history.push(path)}
                    shouldBlockNavigation={(location) => {
                      if (this.state.isFormDataFilled === true) {
                        return true;
                      }
                      return false;
                    }}
                  />
                </Col>
              </Row>
            ) : null}
          </Grid>
        </>
        {this.renderServicNumberStatusDialog()}
        {this.renderNumbersSyncDialog()}
      </PageLoader>
    );
  }
}

const mapStateToProps = (state) => ({
  serviceNumbersAllRecords: state.serviceNumberStore.serviceNumbersAllRecords,
  serviceNumbersDisplayRecords:
    state.serviceNumberStore.serviceNumbersDisplayRecords,
  subscriberAccount: state.subscriberStore.subscriberAccount,
});

const mapActionToProps = {
  setServiceNumbersAllRecords,
  setServiceNumbersDisplayRecords,
  setServiceNumberPhoneNumberToAlter,
  setServiceNumberRoutingPolicyToAlter,
  setServiceNumbersUpdateRecords,
  updateServiceNumberPhoneNumber,
  updateServiceNumberRoutingPolicy,
  resetServiceNumbersData,
  showPageWaitDialog,
  hidePageWaitDialog,
};

export default connect(mapStateToProps, mapActionToProps)(ServiceNumberPage);
